import type { ImportRecord, Item, Room, Section } from '../db/schema';

/**
 * What a `.visilist` file is, written down once so the writer and the reader
 * can't drift.
 *
 *   manifest.json        schema version, exported date, counts
 *   data.json            rooms, sections, items, imports — everything but binaries
 *   images/<id>.<ext>    every blob, original and thumbnail
 *
 * Record ids are permanent (invariant 5), which is the whole reason merge can
 * key on them: re-importing your own backup finds everything already there.
 */

export const MANIFEST = 'manifest.json';
export const DATA = 'data.json';
export const IMAGES = 'images/';

/** Bumped when `Backup` below stops being readable by the previous reader. */
export const SCHEMA_VERSION = 1;

/** Not a name a zip of holiday photos would carry. */
export const FORMAT = 'visilist-backup';

export type Counts = {
  rooms: number;
  sections: number;
  items: number;
  images: number;
  imports: number;
};

export type Manifest = {
  format: typeof FORMAT;
  schemaVersion: number;
  exportedAt: number;
  /** False for a backup of a selection rather than of everything. */
  complete: boolean;
  counts: Counts;
};

/**
 * An image's row without its two blobs, plus where in the zip they went. The
 * names are stored rather than derived so that a future change to how they're
 * built doesn't strand every backup written before it.
 */
export type ImageMeta = {
  id: string;
  w: number;
  h: number;
  mime: string;
  addedAt: number;
  file: string;
  thumbFile: string;
};

export type Backup = {
  rooms: Room[];
  sections: Section[];
  items: Item[];
  imports: ImportRecord[];
  images: ImageMeta[];
};

/**
 * `settings` is deliberately absent. The only thing in that table is whether
 * this machine has been asked about persistent storage, which is about the
 * machine and not about the user's things.
 */

const EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/avif': 'avif',
};

/** The extension is a courtesy to whoever opens the zip by hand. */
export function imageFile(id: string, mime: string): string {
  return `${IMAGES}${id}.${EXTENSIONS[mime] ?? 'bin'}`;
}

/** Thumbnails are always WebP — `db/images.ts` encodes them on a canvas. */
export function thumbFile(id: string): string {
  return `${IMAGES}${id}.thumb.webp`;
}

/** `visilist-2026-08-02.visilist`, which sorts usefully in a folder. */
export function backupName(at: number, partial = false): string {
  const date = new Date(at).toISOString().slice(0, 10);
  return `visilist-${date}${partial ? '-selection' : ''}.visilist`;
}

export function isManifest(value: unknown): value is Manifest {
  if (typeof value !== 'object' || value === null) return false;
  const manifest = value as Partial<Manifest>;
  return manifest.format === FORMAT && typeof manifest.schemaVersion === 'number';
}
